import {
  Box,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Modal,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FaMapMarkerAlt, FaRoute, FaTimes, FaTruck } from "react-icons/fa";
import { PorterDeliveryTrackingStatus } from "../shipment.interface";
import { GetStatusBadge } from "./GetStatusBadge";
import {
  formatDateForNotification,
  formatTime,
} from "../../../utils/dateResolvers";
import { GaraazTextComponent, GaraazCopyText } from "@svan-garaaz/reactsharedlib";

export interface TrackingDetails {
  trackingUrl: string;
  awbNo: string;
  shipmentNo: string;
  status?: PorterDeliveryTrackingStatus;
  lastUpdatedAt?: Date;
}

interface TrackingModalProps {
  isOpen: boolean;
  onClose: () => void;
  trackingDetails: TrackingDetails;
}

function TrackingModal({
  isOpen,
  onClose,
  trackingDetails,
}: TrackingModalProps) {
  const lastUpdatedAt = trackingDetails.lastUpdatedAt
    ? `${formatTime(trackingDetails.lastUpdatedAt)} - ${formatDateForNotification(trackingDetails.lastUpdatedAt)}`
    : "";
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
      <ModalOverlay />
      <ModalContent maxW="6xl" h="95vh" maxH="95vh" m={4} borderRadius="xl">
        {/* Header */}
        <Box
          bg="linear-gradient(135deg, #dbeafe 0%, #93c5fd 50%, #60a5fa 100%)"
          color="white"
          p={6}
          borderTopRadius="xl"
        >
          <Flex align="center" justify="space-between">
            <HStack spacing={4}>
              <Box
                w={12}
                h={12}
                display="flex"
                alignItems="center"
                justifyContent="center"
                bg="whiteAlpha.200"
                borderRadius="lg"
              >
                <Icon as={FaRoute} boxSize={8} color="blue.500" />
              </Box>
              <VStack align="start" spacing={1} color="gray.800">
                <HStack spacing={2} align="center">
                  <Heading size="md" fontWeight="bold">
                    {trackingDetails.shipmentNo}
                  </Heading>
                  <GaraazCopyText 
                    text={trackingDetails.shipmentNo}
                    popOverMessage="Copied"
                  />
                </HStack>
              </VStack>
            </HStack>
            <HStack spacing={4}>
              {trackingDetails.status && (
                <GetStatusBadge status={trackingDetails.status} size="small" />
              )}
              <IconButton
                aria-label="Close modal"
                icon={<FaTimes />}
                variant="ghost"
                color="whiteAlpha.800"
                _hover={{ color: "white", bg: "whiteAlpha.100" }}
                onClick={onClose}
                size="lg"
              />
            </HStack>
          </Flex>
        </Box>

        {/* Quick Info Cards */}
        <Box
          h="100%"
          w="100%"
          borderBottomRadius="xl"
          paddingBottom={"11px"}
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
        >
          {trackingDetails.trackingUrl ? (
            <iframe
              src={trackingDetails.trackingUrl}
              width="100%"
              height="100%"
            />
          ) : (
            <HStack spacing={2} mb={6}>
              <Icon as={FaRoute} color="blue.600" boxSize={5} />
              <Heading size="xs" color="gray.900">
                Shipment Tracking Not Available
              </Heading>
            </HStack>
          )}
        </Box>
      </ModalContent>
    </Modal>
  );
}

export default TrackingModal;
